import { Download, Search } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";
import { SearchableSelect } from "@/shared/ui/searchable-select";

import { useCampaigns } from "../api/use-campaigns";
import { usePromotions } from "../api/use-promotions";
import { useRedemptionLedger } from "../api/use-redemption-ledger";
import { PrimaryButton, SecondaryButton } from "../components/buttons";
import { RedemptionTable } from "../components/RedemptionTable";
import { redemptionToCsv } from "../lib/redemption-csv";
import { downloadCsv } from "../lib/report-csv";

type RedemptionRow = components["schemas"]["RedemptionRow"];

/** Client-side text filter across order id, seller, campaign and promotion. */
function matchesSearch(row: RedemptionRow, query: string): boolean {
  const q = query.toLowerCase();
  return (
    row.order_id.toLowerCase().includes(q) ||
    (row.seller_id ?? "").toLowerCase().includes(q) ||
    (row.campaign_name ?? "").toLowerCase().includes(q) ||
    (row.promotion_name ?? "").toLowerCase().includes(q)
  );
}

export function RedemptionLedgerPage(): JSX.Element {
  const { t } = useTranslation(["promotion", "nav", "common"]);
  // Campaign / promotion are real server-side filters (BE supports
  // campaign_id + promotion_id); the promotion list follows the chosen campaign.
  const [campaignId, setCampaignId] = useState("");
  const [promotionId, setPromotionId] = useState("");
  const { data, isLoading, isError } = useRedemptionLedger({
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...(promotionId ? { promotion_id: promotionId } : {}),
  });
  const { data: campaignData } = useCampaigns();
  const { data: promotionData } = usePromotions(campaignId ? { campaign_id: campaignId } : {});
  const campaignOptions = campaignData?.items ?? [];
  const promotionOptions = promotionData?.items ?? [];
  // Draft = live input; applied = committed on Search/Clear (matches the mockup).
  const [draftSearch, setDraftSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  const allItems = useMemo(() => data?.items ?? [], [data]);
  const items = useMemo(
    () => (appliedSearch ? allItems.filter((r) => matchesSearch(r, appliedSearch)) : allItems),
    [allItems, appliedSearch],
  );
  const pageItems = useMemo(() => items.slice(offset, offset + limit), [items, offset, limit]);

  const totalSets = items.reduce((sum, r) => sum + r.redeem_set, 0);
  const totalGiftQty = items.reduce((sum, r) => sum + r.gwp_quantity, 0);

  function applySearch(value: string): void {
    setAppliedSearch(value);
    setOffset(0);
  }

  function handleExport(): void {
    downloadCsv(
      `redemption-ledger-${new Date().toISOString().slice(0, 10)}.csv`,
      redemptionToCsv(items),
    );
    toast.success(t("redemption.export_done", { count: items.length }));
  }

  function handleClear(): void {
    setDraftSearch("");
    setAppliedSearch("");
    setOffset(0);
  }

  return (
    <div data-testid="redemption-ledger-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.promotion")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("promotion:redemption.title")}</span>
          </>
        }
        title={t("promotion:redemption.title")}
        subtitle={t("promotion:redemption.subtitle")}
      />

      {/* Context header card: campaign / promotion + redeemed-set / gift-qty totals. */}
      <div
        className="mb-4 flex flex-wrap items-end gap-3.5 rounded-2xl border border-[var(--premium-border)] bg-white px-4 py-3.5 shadow-[var(--sh-card)]"
        data-testid="redemption-context"
      >
        <label className="flex min-w-[180px] flex-col gap-1 text-[11px] font-semibold text-[var(--text-secondary)]">
          {t("redemption.campaign_label")}
          <SearchableSelect
            data-testid="redemption-campaign"
            aria-label={t("redemption.campaign_label")}
            className="w-[240px]"
            value={campaignId}
            options={campaignOptions.map((c) => ({ value: c.id, label: c.name }))}
            placeholder={t("redemption.all_campaigns")}
            emptyText={t("common:no_results")}
            clearLabel={t("common:clear")}
            onChange={(next) => {
              setCampaignId(next);
              setPromotionId("");
              setOffset(0);
            }}
          />
        </label>
        <label className="flex min-w-[200px] flex-col gap-1 text-[11px] font-semibold text-[var(--text-secondary)]">
          {t("redemption.promotion_label")}
          <SearchableSelect
            data-testid="redemption-promotion"
            aria-label={t("redemption.promotion_label")}
            className="w-[260px]"
            value={promotionId}
            options={promotionOptions.map((p) => ({ value: p.id, label: p.name }))}
            placeholder={t("redemption.all_promotions")}
            emptyText={t("common:no_results")}
            clearLabel={t("common:clear")}
            onChange={(next) => {
              setPromotionId(next);
              setOffset(0);
            }}
          />
        </label>
        <div className="ml-auto flex flex-wrap items-end gap-2">
          <div
            className="flex flex-col items-center gap-0.5 rounded-[10px] border border-[var(--brand-200)] bg-[var(--brand-50)] px-3.5 py-2"
            data-testid="redemption-total-sets"
          >
            <span className="num text-[20px] font-extrabold tabular-nums text-[var(--brand-700)]">
              {totalSets.toLocaleString()}
            </span>
            <span className="whitespace-nowrap text-[10px] font-semibold text-[var(--brand-700)]">
              {t("redemption.total_sets")}
            </span>
          </div>
          <div
            className="flex flex-col items-center gap-0.5 rounded-[10px] border border-[var(--intent-success-border)] bg-[var(--intent-success-bg)] px-3.5 py-2"
            data-testid="redemption-total-gift-qty"
          >
            <span className="num text-[20px] font-extrabold tabular-nums text-[var(--intent-success-fg)]">
              {totalGiftQty.toLocaleString()}
            </span>
            <span className="whitespace-nowrap text-[10px] font-semibold text-[var(--intent-success-fg)]">
              {t("redemption.total_gift_qty")}
            </span>
          </div>
        </div>
      </div>

      {/* Search toolbar: live input + Export / Clear / Search actions. */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5" data-testid="redemption-toolbar">
        <div className="relative min-w-[200px] max-w-[320px] flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
          />
          <input
            type="search"
            data-testid="redemption-search"
            placeholder={t("redemption.search_placeholder")}
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch(draftSearch);
            }}
            className="focus-ring h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-10 pr-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition placeholder:text-[var(--text-subtle)] focus:border-[var(--brand-500)]"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <SecondaryButton
            type="button"
            data-testid="redemption-export"
            onClick={handleExport}
            disabled={items.length === 0}
          >
            <Download className="h-4 w-4" aria-hidden />
            {t("redemption.export")}
          </SecondaryButton>
          <SecondaryButton
            type="button"
            data-testid="redemption-clear"
            onClick={handleClear}
            disabled={!draftSearch && !appliedSearch}
          >
            {t("redemption.clear")}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            data-testid="redemption-search-button"
            onClick={() => applySearch(draftSearch)}
          >
            <Search className="h-4 w-4" aria-hidden />
            {t("redemption.search")}
          </PrimaryButton>
        </div>
      </div>

      <RedemptionTable items={pageItems} isLoading={isLoading} isError={isError} />

      {!isLoading && !isError && (
        <Pagination
          total={items.length}
          offset={offset}
          limit={limit}
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={({ offset: nextOffset, limit: nextLimit }) => {
            setOffset(nextOffset);
            setLimit(nextLimit);
          }}
        />
      )}
    </div>
  );
}
